import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

export interface OrgWorkspaceChannel {
  type: string;
  name: string | null;
  phone: string | null;
  username: string | null;
}

export interface OrgWorkspaceRow {
  publicId: string;
  name: string;
  /** Reseller-side identifier for customer workspaces; null for team rows. */
  externalId: string | null;
  createdAt: string;
  channelCount: number;
  /** Usage this quota period, in the org's meter (AIT-436). */
  used: number;
  /** True when any channel in the workspace is still forwarding. */
  forwardingEnabled: boolean;
  enabledChannelCount: number;
  /** Meta-connected channels — a workspace with none can't be turned on. */
  connectedChannelCount: number;
  /** Soft-deleted (deprecated) row — inert in the UI, shown only on demand. */
  deprecated: boolean;
  channels: OrgWorkspaceChannel[];
}

interface OrgWorkspacesResponse {
  /** The meter every row's `used` is counted in. */
  usageUnit: 'messages' | 'actions';
  workspaces: OrgWorkspaceRow[];
}

/** Wire union of DELETE /workspaces/:id (AIT-198) — mirror of the backend type. */
export type WorkspaceDeleteResult =
  | { result: 'deleted' }
  | { result: 'deprecated'; channelsDisconnected: number };

// POST /workspaces returns the public workspace DTO (AIT-147) — `id` is the
// ws_ public id, not a raw uuid.
interface CreatedWorkspaceResponse {
  id: string;
  name: string;
  memberCount: number;
  channelCount: number;
}

interface SetForwardingVars {
  enabled: boolean;
  workspaceIds: string[] | null;
  /** Sent when workspaceIds is null so Enable All scopes to this surface's kind. */
  kind: 'team' | 'customer';
}

export function useOrgWorkspaces(
  orgId: string | undefined,
  kind: 'team' | 'customer',
  includeDeprecated = false,
) {
  return useApiQuery<OrgWorkspacesResponse>(
    // Same key shape as org-team's workspace-options query (same endpoint +
    // ?kind slice), so both surfaces share one cache entry per kind. The
    // deprecated view is its own key slice; the shared 'workspaces' prefix
    // means one invalidate covers both.
    includeDeprecated
      ? qk.org(orgId ?? '', 'workspaces', kind, 'deprecated')
      : qk.org(orgId ?? '', 'workspaces', kind),
    `/organizations/${orgId}/workspaces?kind=${kind}${includeDeprecated ? '&includeDeprecated=true' : ''}`,
    { enabled: !!orgId },
  );
}

function useOrgWorkspacesInvalidate(orgId: string | undefined) {
  const qc = useQueryClient();
  return () =>
    orgId
      ? qc.invalidateQueries({ queryKey: qk.org(orgId, 'workspaces') })
      : undefined;
}

export function useCreateWorkspace(orgId: string | undefined) {
  const invalidate = useOrgWorkspacesInvalidate(orgId);
  return useApiMutation<CreatedWorkspaceResponse, { name: string }>(
    ({ name }) => ({
      path: '/workspaces',
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      },
      fallback: 'Failed to create workspace',
    }),
    { onSuccess: invalidate },
  );
}

export function useSetOrgForwarding(orgId: string | undefined) {
  const invalidate = useOrgWorkspacesInvalidate(orgId);
  return useApiMutation<void, SetForwardingVars>(
    ({ enabled, workspaceIds, kind }) => ({
      path: `/organizations/${orgId}/forwarding`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          workspaceIds ? { enabled, workspaceIds } : { enabled, kind },
        ),
      },
      fallback: 'Failed to update messaging',
    }),
    { onSuccess: invalidate },
  );
}

export function useReclassifyWorkspace(orgId: string | undefined) {
  const invalidate = useOrgWorkspacesInvalidate(orgId);
  return useApiMutation<void, { workspaceId: string; kind: 'team' | 'customer' }>(
    ({ workspaceId, kind }) => ({
      path: `/organizations/${orgId}/workspaces/${workspaceId}/kind`,
      init: {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind }),
      },
      fallback: 'Could not move this workspace.',
    }),
    { onSuccess: invalidate },
  );
}

export function useDeleteWorkspace(orgId: string | undefined) {
  const invalidate = useOrgWorkspacesInvalidate(orgId);
  return useApiMutation<WorkspaceDeleteResult, { workspaceId: string }>(
    ({ workspaceId }) => ({
      path: `/workspaces/${workspaceId}`,
      init: { method: 'DELETE' },
      fallback: 'Could not delete this workspace.',
    }),
    { onSuccess: invalidate },
  );
}
